/**
 * VibeCheck push helper — request permission, subscribe to PushManager,
 * and POST the resulting subscription to the backend.
 *
 * Anonymous-friendly: the backend keys subscriptions on `X-Device-Id` (already
 * set by `lib/api.js`) so a logged-out player still gets pushes for their room.
 */
import { api } from "./api";

const STORAGE_KEY = "vc_push_subscribed_v1";

/** Convert a base64url-encoded VAPID key to a Uint8Array (PushManager expects this). */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  if (typeof Notification === "undefined") return "default";
  return Notification.permission; // "default" | "granted" | "denied"
}

/** Have we already completed the subscribe handshake on this device? */
export function pushAlreadySubscribed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

export function markPushSubscribed() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Run the full subscribe handshake. Caller is responsible for the
 * pre-prompt UX — this function ALWAYS triggers the native permission
 * dialog if it hasn't been answered yet.
 *
 * Returns true on success, false otherwise.
 */
export async function subscribeToPush() {
  if (!pushSupported()) return false;

  // 1. Permission
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return false;

  // 2. Service worker ready
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return false;

  // 3. Fetch VAPID public key from backend
  let publicKey = "";
  try {
    const res = await api.client.get("/push/public-key");
    publicKey = res.data?.public_key || "";
  } catch (_e) {
    return false;
  }
  if (!publicKey) return false;

  // 4. Subscribe (or reuse existing subscription)
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (_e) {
      return false;
    }
  }

  // 5. Send to backend
  try {
    await api.client.post("/push/subscribe", { subscription: subscription.toJSON() });
    markPushSubscribed();
    return true;
  } catch (_e) {
    return false;
  }
}
